import { createStore, del, get, set } from 'idb-keyval';

const store = createStore('ectopics-outbox', 'queue');
const QUEUE_KEY = 'queue';

export type OutboxEndpoint = '/api/incidents' | '/api/rollups';

export type OutboxItem = {
  key: string;
  endpoint: OutboxEndpoint;
  payload: unknown;
  enqueuedAt: number;
  attempts: number;
  lastError?: string;
};

export type SubmitResult =
  | { status: 'sent'; response: unknown }
  | { status: 'queued'; error?: string }
  | { status: 'rejected'; error: string };

type Listener = (size: number) => void;
const listeners = new Set<Listener>();

async function readQueue(): Promise<OutboxItem[]> {
  return ((await get(QUEUE_KEY, store)) as OutboxItem[] | undefined) ?? [];
}

async function writeQueue(q: OutboxItem[]): Promise<void> {
  if (q.length === 0) await del(QUEUE_KEY, store);
  else await set(QUEUE_KEY, q, store);
  for (const cb of listeners) cb(q.length);
}

export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  void size().then(cb);
  return () => {
    listeners.delete(cb);
  };
}

export async function size(): Promise<number> {
  return (await readQueue()).length;
}

export async function peek(): Promise<OutboxItem[]> {
  return readQueue();
}

async function postItem(item: OutboxItem): Promise<Response> {
  return fetch(item.endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      'idempotency-key': item.key,
    },
    body: JSON.stringify(item.payload),
  });
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function submit(
  endpoint: OutboxEndpoint,
  payload: unknown,
  key: string = newIdempotencyKey(),
): Promise<SubmitResult> {
  const item: OutboxItem = {
    key,
    endpoint,
    payload,
    enqueuedAt: Date.now(),
    attempts: 0,
  };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const q = await readQueue();
    q.push(item);
    await writeQueue(q);
    return { status: 'queued' };
  }

  try {
    const res = await postItem(item);
    if (res.ok) {
      const body = (await res.json().catch(() => null)) as unknown;
      return { status: 'sent', response: body };
    }
    if (res.status >= 400 && res.status < 500) {
      const text = await res.text().catch(() => '');
      return {
        status: 'rejected',
        error: `${res.status} ${res.statusText}: ${text || '(no body)'}`,
      };
    }
    const q = await readQueue();
    q.push({ ...item, lastError: `server ${res.status}` });
    await writeQueue(q);
    return { status: 'queued', error: `server ${res.status}` };
  } catch (err) {
    const q = await readQueue();
    q.push({ ...item, lastError: (err as Error).message });
    await writeQueue(q);
    return { status: 'queued', error: (err as Error).message };
  }
}

let draining = false;

export async function drain(): Promise<{ sent: number; remaining: number }> {
  if (draining) return { sent: 0, remaining: await size() };
  draining = true;
  let sent = 0;
  try {
    while (true) {
      const q = await readQueue();
      if (q.length === 0) return { sent, remaining: 0 };
      const head = q[0]!;
      let result: 'ok' | 'drop' | 'retry' = 'retry';
      try {
        const res = await postItem(head);
        if (res.ok) result = 'ok';
        else if (res.status >= 400 && res.status < 500) result = 'drop';
        else {
          head.attempts += 1;
          head.lastError = `server ${res.status}`;
          result = 'retry';
        }
      } catch (err) {
        head.attempts += 1;
        head.lastError = (err as Error).message;
        result = 'retry';
      }
      if (result === 'retry') {
        q[0] = head;
        await writeQueue(q);
        return { sent, remaining: q.length };
      }
      q.shift();
      await writeQueue(q);
      if (result === 'ok') sent += 1;
    }
  } finally {
    draining = false;
  }
}

let triggersInstalled = false;

export function installSyncTriggers(): void {
  if (triggersInstalled || typeof window === 'undefined') return;
  triggersInstalled = true;
  const tryDrain = () => {
    void drain();
  };
  window.addEventListener('online', tryDrain);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryDrain();
  });
  setTimeout(tryDrain, 250);
}
